import { useState, useEffect } from 'react';
import axios from "axios";
import moment from 'moment';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface PayloadType {
    [key: string]: any;
}

const FeeExportModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    userPermissions: any;
}> = ({ setSwalProps, isOpen, onClose, userPermissions }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [subaccountList, setSubaccountList] = useState<any>([]);
    const [isOpenDatePanel, setIsOpenDatePanel] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [dateToExport, setDateToExport] = useState<Date>(null);
    const [receiverEmail, setReceiverEmail] = useState<string>("");
    const [subaccountid, setSubaccountid] = useState<string>("");
    const userRole = localStorage.getItem("userRole")?.toLowerCase();

    const isFormValid = receiverEmail !== "" && dateToExport;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await fetchSubaccounts();
            setIsLoading(false);
        };

        fetchData();
    }, []);

    const fetchSubaccounts = async () => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL +
                `/api/subaccount/get-all-subaccounts`
            );

            const data = response.data.subaccounts?.payload;

            setSubaccountList(data);
        } catch (error) {
            console.error("Error fetching subaccounts", error);
        }
    };

    const closeModal = async () => {
        setDateToExport(null);
        setReceiverEmail("");
        setSubaccountid("");
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const firstAndLastDateOfMonth = getFirstAndLastDateOfMonth(dateToExport);
            const formattedFromDate = moment(firstAndLastDateOfMonth.firstDate, 'DD/MM/YYYY').format('YYYY-MM-DD 00:00');
            const formattedToDate = moment(firstAndLastDateOfMonth.lastDate, 'DD/MM/YYYY').format('YYYY-MM-DD 23:59');
            const queryObject: PayloadType = {
                is_export: true,
                email: receiverEmail,
                from: formattedFromDate,
                to: formattedToDate,
                userRole: userRole,
                username: localStorage.getItem("username")
            };

            if (subaccountid !== "") {
                if (userRole === "superadmin") {
                    queryObject.subaccount_id = subaccountid;
                }
            } else {
                if (userRole !== "superadmin" && userPermissions?.exportTransaction) {
                    queryObject.subaccount_id = localStorage.getItem("subaccountId");
                }
            }

            const queryString = Object.keys(queryObject)
                .map(
                    (key) =>
                        `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
                )
                .join("&");

            await axios.get(process.env.REACT_APP_API_URL + `/api/transaction/get-all-transactions?${queryString}`)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Your request has been successfully submitted",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: "Your request could not be submitted successfully, please try again",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                            },
                        });
                    }
                })
                .catch(function (error) {
                    console.log('error.response: ', error.response);
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const getFirstAndLastDateOfMonth = (date: Date) => {
        // First date of the month
        const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);

        // Last date of the month
        const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        return { firstDate, lastDate };
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                    <ModalBody className="p-6 md:p-7">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Export Transaction
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Select a month and provide an email to receive the export report
                                </p>
                            </div>
                        </div>

                        <div className="mb-6 space-y-4">
                            <div className="export-date-picker-wrapper">
                                <label
                                    htmlFor="dateToExport"
                                    className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100"
                                >
                                    Month & year you'd like to export <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    disabled={isProcessing}
                                    selected={dateToExport}
                                    onInputClick={() => setIsOpenDatePanel(!isOpenDatePanel)}
                                    onSelect={() => setIsOpenDatePanel(false)}
                                    onChange={(date: any) => setDateToExport(date)}
                                    dateFormat="MMM, yyyy"
                                    placeholderText="Please select"
                                    showMonthYearPicker
                                    open={isOpenDatePanel}
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white !cursor-pointer"
                                    onKeyDown={(e) => e.preventDefault()}
                                    readOnly={true}
                                    maxDate={new Date()}
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="receiverEmail"
                                    className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100"
                                >
                                    Please fill in email to receive report <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="receiverEmail"
                                    type="text"
                                    autoComplete="off"
                                    disabled={isProcessing}
                                    value={receiverEmail}
                                    onChange={(e) => setReceiverEmail(e.target.value)}
                                    placeholder="Enter email address"
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                />
                            </div>

                            {userRole === "superadmin" && (
                                <div>
                                    <label
                                        htmlFor="subaccountid"
                                        className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100"
                                    >
                                        Subaccount
                                    </label>
                                    <select
                                        id="subaccountid"
                                        disabled={isProcessing || isLoading}
                                        value={subaccountid}
                                        onChange={(e) => setSubaccountid(e.target.value)}
                                        className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                    >
                                        <option value="">{isLoading ? "Retrieving..." : "All"}</option>
                                        {subaccountList.map((item: any) => (
                                            <option key={item.id} value={item.id}>{item.suba_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                onClick={() => closeModal()}
                                isDisabled={isProcessing}
                                className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                            >
                                Close
                            </Button>

                            <Button
                                onClick={() => handleSubmit()}
                                isDisabled={!isFormValid || isProcessing}
                                isLoading={isProcessing}
                                loadingText='Processing...'
                                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                            >
                                Export
                            </Button>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default FeeExportModal;